import type { SortDescriptor } from "@/components/kit";

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { CartListTable } from "@/components/carts/table";
import { useGetAllCarts } from "@/hooks/api/cart/useGetAllCarts";
import { useDebounce } from "@/hooks/useDebounce";
import { AppPage } from "@/layouts/page";
import { AttributionFilters } from "@/types";
import { CartSortBy } from "@/types/cart";

export const CartsPage = () => {
  let [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState<AttributionFilters>({});
  const [sort, setSort] = useState<SortDescriptor>({
    column: "total",
    direction: "descending",
  });

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const initialPage = searchParams.get("page")
    ? parseInt(searchParams.get("page")!)
    : 1;

  const [pageParams, setPageParams] = useState({
    page: initialPage,
    limit: 10,
  });

  const { data: carts, isFetching: isCartsLoading } = useGetAllCarts({
    ...pageParams,
    ...filters,
    searchTerm: debouncedSearchTerm,
    sortBy: sort.column as CartSortBy,
    sortOrder: sort.direction === "ascending" ? "ASC" : "DESC",
  });

  useEffect(() => {
    const urlPage = searchParams.get("page");

    if (urlPage) {
      const page = parseInt(urlPage);

      setPageParams((prev) => ({ ...prev, page }));
    } else {
      // If no page in URL, reset to page 1
      setPageParams((prev) => ({ ...prev, page: 1 }));
    }
  }, [searchParams]);

  const handleLimitChange = (limit: number) => {
    setPageParams((prev) => ({ ...prev, limit }));
    handlePageChange(1);
  };

  const handlePageChange = (newPage: number) => {
    searchParams.set("page", newPage.toString());
    setSearchParams(searchParams);
  };

  const handleSearchTermChange = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
  };

  const handleFiltersChange = (next: AttributionFilters) => {
    setFilters(next);
    handlePageChange(1);
  };

  const handleSortChange = (next: SortDescriptor) => {
    setSort(next);
    handlePageChange(1);
  };

  return (
    <AppPage
      subtitle={
        carts ? `${carts.total.toLocaleString()} carts in total` : undefined
      }
      title="Carts"
    >
      <CartListTable
        changePage={handlePageChange}
        filters={filters}
        handleSearchTermChange={handleSearchTermChange}
        isLoading={isCartsLoading}
        items={carts?.data ?? []}
        limit={pageParams.limit}
        page={pageParams.page ?? 1}
        searchTerm={searchTerm}
        sort={sort}
        totalCarts={carts?.total ?? 0}
        onFiltersChange={handleFiltersChange}
        onLimitChange={handleLimitChange}
        onSortChange={handleSortChange}
      />
    </AppPage>
  );
};
